// Het detailpaneel van een regeling.
//
// V1-indeling (r. 463-535: bedrag- en deadline-tegels, omschrijving, hoe
// aanvragen, wie kwalificeert, knop naar de bron), met drie wijzigingen die
// rechtstreeks uit de gemeten data komen:
//
//  1. DE DOELGROEP-EIS STAAT BOVENAAN, niet onderaan bij "wie kwalificeert".
//     Bij 102 van de 119 regelingen is dat de poort: "alleen werkgevers
//     aangesloten bij dit fonds". Wie dat pas onder aan het paneel leest heeft
//     al drie alinea's gelezen over geld dat hij niet kan krijgen.
//  2. DE DEADLINE-TEGEL IS EEN VENSTER met vier standen (lib/venster.ts),
//     want de dummy kon "opent pas op 7 april" niet uitdrukken.
//  3. ONDERAAN DE HERKOMSTREGEL, en bij elk hard veld het letterlijke citaat
//     van de officiële pagina. Wij verifiëren niet wat de bron niet
//     verifieerde; wat we wél kunnen tonen is waar het vandaan komt en wanneer
//     wij er zelf voor het laatst keken.
//
// Een leeg veld toont zijn REDEN (W8-regel). Dat is hier geen franje: de
// officiële pagina's zwijgen vaak over bedrag en periode, en een leeg vak zou
// dat verbergen.

import type { Funding } from "../contracts";
import { SCOPE_UITLEG, scopeLabel } from "../lib/fundingVondst";
import { fmtDatum, statusRegel, vensterStand } from "../lib/venster";
// Kleuren via de chattokens: in de oude weergave dezelfde waarden, in de
// nieuwe volgen ze licht en donker (testfeedback 03-11, rij 6).
import { color, radius, size, weight } from "./chatTokens";
import { EmptyReason } from "./Empty";
import { DekkingLabel, FundingAanvulling } from "./FundingAanvulling";
import { PanelHeader, SectionLabel } from "./ui";

/** Waarom een veld leeg is, in gewone taal. De vlaggen komen uit de leespas. */
const VLAG_UITLEG: Record<string, string> = {
  bedrag_niet_vermeld: "De officiële pagina noemt geen maximumbedrag in euro's.",
  bedrag_zonder_citaat:
    "Er stond wel een bedrag, maar we konden het niet letterlijk uit de pagina " +
    "citeren. Dan laten we het weg — een bedrag zonder bewijs is hier geen informatie.",
  bedrag_niet_in_bron:
    "Het voorgestelde bedrag was niet als getal in de brontekst terug te vinden " +
    "en is daarom niet overgenomen.",
  bedrag_eenheid_onduidelijk:
    "Onduidelijk of het bedrag per aanvraag of per medewerker geldt.",
  venster_niet_vermeld: "De pagina noemt geen aanvraagperiode.",
  aanvraagperiode_uit_export:
    "De getoonde periode komt uit de redactie-export van vindsubsidies.nl " +
    "(aug 2026), niet van de officiële pagina — die noemt er zelf geen.",
  venster_zonder_citaat:
    "Er stond een periode, maar niet letterlijk citeerbaar. Een deadline zonder " +
    "bewijs laten we weg.",
  venster_zonder_datum: "Er werd een periode genoemd zonder bruikbare datums.",
  einddatum_zonder_datum: "Er werd een einddatum genoemd zonder bruikbare datum.",
  status_niet_op_pagina: "De pagina zegt niet of de regeling open of gesloten is.",
  status_zonder_citaat: "De status was niet letterlijk uit de pagina te citeren.",
  aanvraagmoment_niet_vermeld:
    "De pagina zegt niet of je vooraf of achteraf moet aanvragen.",
  aanvraagmoment_zonder_citaat:
    "Het aanvraagmoment was niet letterlijk uit de pagina te citeren.",
  aanvraagprocedure_niet_vermeld: "De pagina beschrijft de aanvraagprocedure niet.",
  doelgroepeis_niet_vermeld: "De pagina noemt geen voorwaarden voor de doelgroep.",
  geen_omschrijving_op_pagina: "De pagina beschrijft de regeling niet.",
  pagina_bevat_geen_regeling:
    "De officiële pagina in ons register gaat niet over deze regeling zelf — " +
    "meestal een homepage van het fonds. We hebben daar dus niets kunnen lezen.",
  meerdere_regelingen_op_pagina:
    "De pagina beschrijft meerdere regelingen; we hebben de bovenste gelezen.",
  nog_niet_gelezen: "Deze regeling is nog niet gelezen.",
};

/** De host zonder www, plus het laatste padstuk als dat iets zegt. */
function hostVan(url: string): string {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    const laatste = u.pathname.split("/").filter(Boolean).pop() ?? "";
    return laatste && laatste.length <= 28 ? `${host}/${laatste}` : host;
  } catch {
    return url;
  }
}

const MOMENT_TEKST: Record<string, string> = {
  vooraf: "vooraf aanvragen",
  achteraf: "achteraf aanvragen",
  vooraf_en_achteraf: "vooraf of achteraf aanvragen",
  onbekend: "aanvraagmoment onbekend",
};

/** De gevolgde velden van de rem (sof/funding/rem.py VELDEN) in gewone taal. */
const HANDMATIG_VELD: Record<string, string> = {
  status: "de status",
  aanvraagmoment: "het aanvraagmoment",
  venster_soort: "de soort aanvraagperiode",
  venster_start: "de begindatum",
  venster_eind: "de einddatum",
  max_bedrag: "het bedrag",
  bedrag_eenheid: "de eenheid van het bedrag",
  doelgroep_eis: "wie mag aanvragen",
};

function vlagTekst(vlaggen: readonly string[], ...sleutels: string[]): string | null {
  for (const s of sleutels) {
    const treffer = vlaggen.find((v) => v === s || v.startsWith(`${s}:`));
    if (treffer) return VLAG_UITLEG[s] ?? VLAG_UITLEG[treffer] ?? null;
  }
  return null;
}

function Blok({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <SectionLabel>{label}</SectionLabel>
      {children}
    </div>
  );
}

function Tegel({
  label,
  waarde,
  sub,
  kleur,
}: {
  label: string;
  waarde: string;
  sub?: string;
  kleur?: string;
}) {
  return (
    <div
      style={{
        flex: 1,
        background: color.surfaceQuote,
        borderRadius: radius.block,
        padding: "12px 14px",
        minWidth: 0,
      }}
    >
      <div style={{ ...columnLabelStijl }}>{label}</div>
      <div
        style={{
          fontSize: 20,
          fontWeight: weight.bold,
          color: kleur ?? color.ink,
          letterSpacing: "-0.03em",
          lineHeight: 1.15,
          marginTop: 4,
          overflowWrap: "anywhere",
        }}
      >
        {waarde}
      </div>
      {sub && (
        <div style={{ fontSize: size.label, color: color.faint, marginTop: 4 }}>{sub}</div>
      )}
    </div>
  );
}

const columnLabelStijl = {
  fontSize: size.columnLabel,
  fontWeight: weight.medium,
  color: color.faint,
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
};

// De losse "bron →"-knop onder elk citaat is weg (owner-opname 1:52): de
// bronnen staan als pills onder aan het paneel, en die zijn de plek waar je
// naar de officiële pagina gaat. Twee routes naar dezelfde link is ruis.
function Citaat({ citaat }: { citaat: string }) {
  return (
    <blockquote
      style={{
        margin: "6px 0 0",
        padding: "8px 12px",
        background: color.surfaceQuote,
        borderLeft: `2px solid ${color.border}`,
        borderRadius: radius.quote,
        fontSize: size.meta,
        color: color.muted,
        lineHeight: 1.6,
      }}
    >
      “{citaat}”
    </blockquote>
  );
}

export function FundingDetail({
  funding,
  labels,
  onClose,
}: {
  funding: Funding;
  /** key -> label per as, uit de API (config, nooit hardgecodeerd). */
  labels: Record<string, Record<string, string>>;
  onClose: () => void;
}) {
  // W5: de serverstatus wint; venster.ts telt alleen de dagen en maakt op.
  const stand = vensterStand(funding.aanvraagVenster, {
    uitExport: (funding.vlaggen ?? []).includes("aanvraagperiode_uit_export"),
    status: funding.status,
    statusHerkomst: funding.statusHerkomst,
  });
  // Reviewronde W5: in het detail staat 'laatst bevestigd' al in een eigen
  // alinea; 'Status vandaag' toont dus alleen de echte servertekst, anders
  // stond de laatste controle er twee keer.
  const regel = funding.statusTekst ? statusRegel(funding) : null;
  const bedrag = funding.maxBedrag;
  const bewijsVan = (veld: string) => funding.bewijs?.find((b) => b.veld === veld);
  const vlaggen = funding.vlaggen ?? [];
  // Reviewronde K1: een waarde uit de beheertab is door een mens nagekeken,
  // niet van de pagina gelezen. De herkomstregel noemt die velden.
  const handmatig = vlaggen
    .filter((v) => v.startsWith("handmatig:"))
    .map((v) => HANDMATIG_VELD[v.slice("handmatig:".length)] ?? v.slice("handmatig:".length));
  const sectorLabels = labels.sector ?? {};
  const themaLabels = labels.thema ?? {};

  const bedragTekst =
    bedrag.bedrag === null
      ? "op aanvraag"
      : `€ ${bedrag.bedrag.toLocaleString("nl-NL")}`;
  const bedragSub =
    bedrag.bedrag === null
      ? undefined
      : bedrag.eenheid === "per_persoon"
        ? "per persoon"
        : bedrag.eenheid === "totaal"
          ? "per aanvraag"
          : "eenheid onbekend";

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <PanelHeader onClose={onClose}>
        <span
          style={{
            fontSize: size.label,
            fontWeight: weight.medium,
            color: color.soft,
            background: color.surfaceHover,
            padding: "3px 10px",
            borderRadius: radius.pill,
          }}
        >
          {labels.verstrekkerType?.[funding.verstrekkerType] ?? funding.verstrekkerType}
        </span>
        <span
          style={{
            fontSize: size.label,
            fontWeight: weight.medium,
            color: stand.kleur,
            background: stand.achtergrond,
            padding: "3px 10px",
            borderRadius: 4,
          }}
        >
          {stand.tekst}
        </span>
        {/* Funding fase 3 (G1 a): automatisch opgenomen vondst, label tot een
            mens hem in de beheertab opneemt. */}
        {vlaggen.includes("nieuw_niet_nagekeken") && (
          <span
            data-nieuw-niet-nagekeken
            title="De vindlaan vond deze regeling en nam hem automatisch op; een beheerder heeft hem nog niet nagekeken."
            style={{
              fontSize: size.label,
              fontWeight: weight.medium,
              color: color.accentAmber,
              background: color.accentAmberBg,
              padding: "3px 10px",
              borderRadius: radius.pill,
            }}
          >
            nieuw gevonden, nog niet nagekeken
          </span>
        )}
        {/* Koppelreparatie (08-10-2026): geen scholingsregeling of alleen
            voor werknemers (contract funding.scope). */}
        {scopeLabel(funding.scope) && (
          <span
            data-scope={funding.scope?.waarde}
            title={SCOPE_UITLEG}
            style={{
              fontSize: size.label,
              fontWeight: weight.medium,
              color: color.soft,
              background: color.surfaceAlt,
              padding: "3px 10px",
              borderRadius: radius.pill,
            }}
          >
            {scopeLabel(funding.scope)}
          </span>
        )}
      </PanelHeader>

      <div style={{ overflowY: "auto", padding: "20px 24px 40px" }}>
        <h2
          style={{
            fontSize: size.panelTitle,
            fontWeight: weight.bold,
            color: color.ink,
            lineHeight: 1.2,
            letterSpacing: "-0.025em",
            margin: 0,
          }}
        >
          {funding.naam}
        </h2>
        <div style={{ fontSize: size.body, color: color.soft, marginTop: 6 }}>
          {funding.verstrekker === funding.naam
            ? "de lijst noemt geen aparte verstrekker"
            : funding.verstrekker}
        </div>
        {/* Fase 2: "x van 14 velden" (contract funding.dekking). */}
        <div style={{ marginTop: 4 }}>
          <DekkingLabel dekking={funding.dekking} los vlaggen={funding.vlaggen} />
        </div>


        {/* 1 — het venster, direct onder de badgebalk. V1-volgorde: eerst
            WANNEER, dan WIE, dan WAARVOOR, dan HOE, en pas dan het bedrag. */}
        <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
          <Tegel
            label="Aanvraagperiode"
            waarde={stand.tekst}
            sub={
              funding.aanvraagVenster.start && funding.aanvraagVenster.eind
                ? `${fmtDatum(funding.aanvraagVenster.start)} – ${fmtDatum(funding.aanvraagVenster.eind)}`
                : MOMENT_TEKST[funding.aanvraagmoment]
            }
            kleur={stand.kleur}
          />
        </div>
        <div style={{ fontSize: size.label, color: color.faint, marginTop: 6 }}>
          {stand.uitleg}
        </div>
        {bewijsVan("venster") && <Citaat citaat={bewijsVan("venster")!.citaat} />}

        {/* 2 — de poort. Bij 102 van de 119 regelingen is dít de vraag die
            beslist of de rest je nog aangaat, dus hij staat vóór de
            omschrijving en niet onderaan bij "wie kwalificeert". */}
        <div
          style={{
            marginTop: 16,
            background: color.accentAmberBg,
            border: `1px solid #fde68a`,
            borderRadius: radius.block,
            padding: "12px 14px",
          }}
        >
          <div style={{ ...columnLabelStijl, color: color.accentAmber }}>
            Wie mag dit aanvragen
          </div>
          <div
            style={{
              fontSize: size.body,
              color: "#78350f",
              lineHeight: 1.6,
              marginTop: 4,
            }}
          >
            {funding.doelgroepEis ?? (
              <span style={{ color: color.accentAmber }}>
                {vlagTekst(vlaggen, "doelgroepeis_niet_vermeld", "pagina_bevat_geen_regeling") ??
                  "De pagina noemt geen voorwaarden."}
              </span>
            )}
          </div>
          {bewijsVan("doelgroep_eis") && (
            <Citaat
              citaat={bewijsVan("doelgroep_eis")!.citaat}
            />
          )}
        </div>

        <div style={{ marginTop: 24 }}>
          <Blok label="Waar is dit voor">
            {funding.omschrijving ? (
              <p style={{ fontSize: size.lead, color: color.muted, lineHeight: 1.75, margin: 0 }}>
                {funding.omschrijving}
              </p>
            ) : (
              <EmptyReason
                reason={
                  vlagTekst(vlaggen, "geen_omschrijving_op_pagina",
                    "pagina_bevat_geen_regeling") ??
                  "De officiële pagina beschrijft de regeling niet."
                }
              />
            )}
          </Blok>

          <Blok label="Hoe aanvragen">
            {funding.hoeAanvragen ? (
              <p style={{ fontSize: size.lead, color: color.muted, lineHeight: 1.75, margin: 0 }}>
                {funding.hoeAanvragen}
              </p>
            ) : (
              <EmptyReason
                reason={
                  vlagTekst(vlaggen, "aanvraagprocedure_niet_vermeld",
                    "pagina_bevat_geen_regeling") ??
                  "De pagina beschrijft de aanvraagprocedure niet."
                }
              />
            )}
            {bewijsVan("hoe_aanvragen") && (
              <Citaat
                citaat={bewijsVan("hoe_aanvragen")!.citaat}
              />
            )}
            <div style={{ fontSize: size.meta, color: color.body, marginTop: 10 }}>
              {MOMENT_TEKST[funding.aanvraagmoment] ?? funding.aanvraagmoment}
              {funding.aanvraagmoment === "vooraf" && (
                <span style={{ color: color.accentAmber, fontWeight: weight.medium }}>
                  {" "}
                  — meld je aan vóór de scholing begint
                </span>
              )}
            </div>
          </Blok>

          {/* Het bedrag sluit af, niet opent. Bij 63 van de 119 regelingen
              noemt de officiële pagina er geen; dat als eerste tegel tonen zet
              het paneel op een leeg vak. */}
          <Blok label="Hoeveel">
            <div style={{ display: "flex", gap: 10 }}>
              <Tegel label="Max bedrag" waarde={bedragTekst} sub={bedragSub} />
            </div>
            {bedrag.bedrag === null && (
              <EmptyReason
                reason={
                  vlagTekst(vlaggen, "bedrag_niet_vermeld", "bedrag_zonder_citaat",
                    "bedrag_niet_in_bron", "pagina_bevat_geen_regeling") ??
                  "Geen bedrag op de officiële pagina."
                }
                style={{ marginTop: 8 }}
              />
            )}
            {bewijsVan("bedrag") && <Citaat citaat={bewijsVan("bedrag")!.citaat} />}
          </Blok>

          {/* Fase 2 (G3 a): de tien velden, elk met citaat of reden. */}
          <FundingAanvulling funding={funding} />

          <Blok label="Voor welke sectoren">
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {funding.sectorbreed ? (
                <span style={chipStijl}>Sectorbreed — geldt voor elke sector</span>
              ) : funding.sectoren.length ? (
                funding.sectoren.map((s) => (
                  <span key={s} style={chipStijl}>
                    {sectorLabels[s] ?? s}
                  </span>
                ))
              ) : (
                <EmptyReason reason="Geen sector toegekend in het register." />
              )}
            </div>
          </Blok>

          <Blok label="Thema">
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {funding.thema.length ? (
                funding.thema.map((t) => (
                  <span key={t} style={chipStijl}>
                    {themaLabels[t] ?? t}
                  </span>
                ))
              ) : (
                <EmptyReason reason="Geen thema toegekend in het register." />
              )}
            </div>
            <p style={{ fontSize: size.label, color: color.faint, marginTop: 8, margin: "8px 0 0" }}>
              Er staan bewust geen vaardigheden bij deze regeling. Regelingen financieren
              scholing in het algemeen, niet een bepaalde vaardigheid; een koppeling zou
              verzonnen zijn.
            </p>
          </Blok>

          {/* 3 — de herkomstregel. */}
          <div
            style={{
              borderTop: `1px solid ${color.lineFaint}`,
              paddingTop: 14,
              marginTop: 4,
            }}
          >
            <SectionLabel>Waar dit vandaan komt</SectionLabel>
            <p style={{ fontSize: size.meta, color: color.soft, lineHeight: 1.7, margin: 0 }}>
              De regeling staat in ons register ({funding.herkomst.register});{" "}
              {handmatig.length ? "de rest" : "alles"} op deze
              kaart is door ons van de officiële pagina('s) gelezen
              {funding.herkomst.laatstGecontroleerd
                ? `, voor het laatst gecontroleerd op ${fmtDatum(funding.herkomst.laatstGecontroleerd)}`
                : ""}
              .
              {handmatig.length > 0 && (
                <span data-testid="handmatig">
                  {" "}
                  Nagekeken in de beheertab, door een mens vastgesteld: {handmatig.join(", ")}.
                </span>
              )}
              {funding.statusHerkomst === "afgeleid" && (
                <>
                  {" "}
                  De status is afgeleid uit het aanvraagvenster — de pagina zei het niet zelf.
                </>
              )}
              {funding.statusHerkomst === "export" && (
                <>
                  {" "}
                  De status en aanvraagperiode komen uit de redactie-export van
                  vindsubsidies.nl (aug 2026) — de officiële pagina noemt ze zelf niet.
                </>
              )}
            </p>
            {/* W5: de status op de dag van lezen en de laatste geslaagde
                controle, onder de herkomstregel. De tekst komt letterlijk van
                de server (sof.funding.status); hier wordt niets bijgerekend. */}
            {regel && (
              <p
                data-testid="statustekst"
                style={{ fontSize: size.meta, color: color.soft, lineHeight: 1.7, margin: "8px 0 0" }}
              >
                <span style={{ fontWeight: weight.medium, color: color.body }}>Status vandaag: </span>
                {regel}
              </p>
            )}
            {funding.herkomst.laatstBevestigd !== undefined && (
              <p style={{ fontSize: size.label, color: color.faint, lineHeight: 1.6, margin: "4px 0 0" }}>
                {funding.herkomst.laatstBevestigd
                  ? `Laatst bevestigd op ${fmtDatum(funding.herkomst.laatstBevestigd)} (jongste geslaagde controle van de officiële pagina).`
                  : "Laatst bevestigd: geen geslaagde controle van de officiële pagina bekend."}
              </p>
            )}
            {/* Betrouwbaar register (08-10-2026): is de bron de actuele
                officiële pagina, en hoe vers? Tekst van de server
                (sof.readapi.funding.map_bronoordeel); hier niets bijgerekend. */}
            {funding.bronoordeel && (
              <p
                data-testid="bronoordeel"
                style={{ fontSize: size.label, color: funding.bronoordeel.telt ? color.faint : color.accentAmber, lineHeight: 1.6, margin: "4px 0 0" }}
              >
                Bron: {funding.bronoordeel.label}
                {funding.bronoordeel.paginadatum ? ` (pagina van ${fmtDatum(funding.bronoordeel.paginadatum)})` : ""}.
                {!funding.bronoordeel.telt && funding.bronoordeel.reden ? ` ${funding.bronoordeel.reden}` : ""}
              </p>
            )}
            {/* Bronnen als de standaard-pills van het huis, met een DIRECTE
                link (owner-opname 1:52). Een rauwe URL van 120 tekens is geen
                pill maar een regel tekst; de host is wat je herkent. */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
              {funding.bronnen.filter((b) => b.resultaat !== "niet_meer_gelinkt").map((b) => {
                const stuk = b.resultaat !== "ok";
                // 08-10-2026: een bron die de bronronde uit de bundel haalde.
                const teltNiet = b.oordeel === "nieuws" || b.oordeel === "andere_regeling" || b.oordeel === "overzicht";
                return (
                  <a
                    key={b.url}
                    href={b.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={
                      stuk
                        ? `${b.url} — ${b.resultaat === "onbereikbaar" ? "niet bereikbaar" : "leeg"}${b.reden ? ` (${b.reden})` : ""}`
                        : b.url
                    }
                    style={{
                      ...chipStijl,
                      color: stuk ? color.accentAmber : teltNiet ? color.faint : color.body,
                      background: stuk ? color.accentAmberBg : color.surfaceHover,
                      textDecoration: "none",
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {hostVan(b.url)}
                    {stuk ? " · niet bereikbaar" : teltNiet ? " · telt niet mee" : " ↗"}
                  </a>
                );
              })}
            </div>
          </div>

          {(funding.bronoordeel?.url ?? funding.bronnen[0]?.url) && (
            <a
              href={funding.bronoordeel?.url ?? funding.bronnen[0].url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                background: color.ink,
                color: "white",
                padding: "14px 16px",
                borderRadius: radius.block,
                textDecoration: "none",
                fontSize: size.body,
                fontWeight: weight.medium,
                marginTop: 20,
              }}
            >
              Naar de officiële pagina
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

const chipStijl = {
  fontSize: size.meta,
  fontWeight: weight.regular,
  color: color.body,
  background: color.surfaceHover,
  border: `1px solid ${color.border}`,
  padding: "5px 12px",
  borderRadius: radius.pill,
} as const;
